/**
 * The donor's redemption codes (phase5-design.md §g b).
 *
 * The management view for the codes a donor minted: state, expiry, the notice
 * banner, and the two funder actions — extend once, ask the platform to
 * distribute. The code itself is deliberately absent: the token was shown
 * exactly once at issue and the server never returns it again, so this page
 * shows the masked hint it derives from the stored hash and nothing more.
 *
 * Every rule the page renders (extendability, the notice window, queue
 * eligibility) is a *preview* of the server's decision, computed through
 * `checkout/codesFlow` — the server answers 409, not this page.
 */
import { ApiRequestError, apiFetch } from '@qalam/shared/frontend';
import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { type CodeState, canAskPlatform, canExtendCode, isNoticeDue } from '../checkout/codesFlow';

export interface MyCodeItem {
  id: string;
  state: CodeState;
  seats: number;
  trackId: string | null;
  expiresAt: string;
  noticeAt: string | null;
  createdAt: string;
  hint: string;
}

export interface MyCodesResponse {
  balanceCents: string;
  seatPriceCents: string;
  /** The grace window the server applies, so the UI derives it from nothing. */
  noticePeriodDays: number;
  items: MyCodeItem[];
}

/** A completed action's feedback, kept per code so rows do not talk over each other. */
interface RowFeedback {
  messageKey: string;
  expiresAt?: string;
}

/** The state chips: one colour family per phase of the code's life. */
function chipStyle(state: CodeState): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-block',
    paddingInline: 'var(--space-2)',
    paddingBlock: 'calc(var(--space-1) / 2)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
  if (state === 'REDEEMED') {
    return { ...base, background: 'var(--qalam-success-100)', color: 'var(--color-success)' };
  }
  if (state === 'EXPIRED' || state === 'CANCELLED') {
    return { ...base, background: 'var(--qalam-danger-100)', color: 'var(--color-danger)' };
  }
  if (state === 'CLAIMED' || state === 'QUEUED') {
    return { ...base, background: 'var(--qalam-accent-300)', color: 'var(--qalam-ink-900)' };
  }
  // ISSUED and NOTICE: still waiting to be used, which is the warning the
  // donor should see.
  return { ...base, background: 'var(--qalam-warning-100)', color: 'var(--color-warning)' };
}

export default function MyCodesPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<MyCodesResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [feedback, setFeedback] = useState<Record<string, RowFeedback>>({});
  const [actionFailed, setActionFailed] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setData(await apiFetch<MyCodesResponse>('/api/my/codes'));
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Fold an action's state changes back into the list, so no reload is needed. */
  function applyResult(id: string, patch: Partial<MyCodeItem>, message: RowFeedback): void {
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            items: current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
          },
    );
    setFeedback((current) => ({ ...current, [id]: message }));
  }

  /** A refusal the donor can act on gets its sentence; anything else the generic one. */
  function refusalMessage(error: unknown): string {
    if (error instanceof ApiRequestError && error.code === 'already_extended') {
      return t('myCodes.alreadyExtended');
    }
    if (error instanceof ApiRequestError && error.code === 'code_state_conflict') {
      return t('myCodes.stateConflict');
    }
    return t('common.error');
  }

  async function extend(id: string): Promise<void> {
    setBusyId(id);
    setActionFailed(null);
    try {
      const result = await apiFetch<{
        state: CodeState;
        expiresAt: string;
        noticeAt: string | null;
      }>(`/api/my/codes/${id}/extend`, { method: 'POST', body: {} });
      applyResult(
        id,
        { state: result.state, expiresAt: result.expiresAt, noticeAt: result.noticeAt },
        { messageKey: 'myCodes.extended', expiresAt: result.expiresAt },
      );
    } catch (error) {
      setActionFailed(refusalMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  async function askPlatform(id: string): Promise<void> {
    setBusyId(id);
    setActionFailed(null);
    try {
      const result = await apiFetch<{ state: CodeState }>(`/api/my/codes/${id}/ask-platform`, {
        method: 'POST',
        body: {},
      });
      applyResult(id, { state: result.state }, { messageKey: 'myCodes.asked' });
    } catch (error) {
      setActionFailed(refusalMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  // The share card's channel: WhatsApp is where a giving link actually
  // travels (ShareCard.tsx), and the copy button is the fallback for whoever
  // is not on WhatsApp.
  const shareUrl = typeof window === 'undefined' ? '' : `${window.location.origin}/give`;

  return (
    <main
      data-testid="my-codes"
      style={{
        maxWidth: 'var(--content-narrow)',
        marginInline: 'auto',
        paddingInline: 'var(--space-4)',
        paddingBlock: 'var(--space-6)',
      }}
    >
      <h1>{t('myCodes.title')}</h1>
      <p>{t('myCodes.intro')}</p>

      {loadFailed ? (
        <p>
          {t('common.error')}{' '}
          <button type="button" onClick={() => void load()}>
            {t('app.retry')}
          </button>
        </p>
      ) : data === null ? (
        <p>{t('myCodes.loading')}</p>
      ) : (
        <>
          <p data-testid="codes-balance">
            {t('myCodes.balance', { amount: (Number(data.balanceCents) / 100).toFixed(2) })}
          </p>
          <Link to="/issue-codes" data-testid="issue-codes-link">
            {t('myCodes.issue')}
          </Link>

          {data.items.length === 0 ? (
            <p data-testid="codes-empty">{t('myCodes.empty')}</p>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                paddingInlineStart: 0,
                display: 'grid',
                gap: 'var(--space-4)',
              }}
            >
              {data.items.map((item) => {
                // A code already extended keeps its row marked for the rest of
                // the visit; the server's audit log is the durable record.
                const alreadyExtended = feedback[item.id]?.messageKey === 'myCodes.extended';
                const extendable = canExtendCode({
                  state: item.state,
                  expiresAt: item.expiresAt,
                  now: new Date(),
                  graceDays: data.noticePeriodDays,
                  alreadyExtended,
                });
                const notice = isNoticeDue({
                  state: item.state,
                  noticeAt: item.noticeAt,
                  now: new Date(),
                });
                // Held in a local so the JSX below narrows it once, instead of
                // re-indexing `feedback` (which TypeScript cannot narrow).
                const rowFeedback = feedback[item.id];
                return (
                  <li
                    key={item.id}
                    data-testid="code-row"
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      paddingInline: 'var(--space-4)',
                      paddingBlock: 'var(--space-3)',
                      display: 'grid',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)',
                        alignItems: 'center',
                      }}
                    >
                      <span style={chipStyle(item.state)}>{item.state}</span>
                      <span>{t('myCodes.seats', { count: item.seats })}</span>
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          direction: 'ltr',
                          unicodeBidi: 'isolate',
                        }}
                      >
                        {item.hint}
                      </span>
                    </div>
                    <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
                      {t('myCodes.expires', {
                        date: new Date(item.expiresAt).toLocaleDateString(),
                      })}
                    </p>
                    {notice ? (
                      <p
                        data-testid="notice-banner"
                        style={{
                          margin: 0,
                          paddingInline: 'var(--space-3)',
                          paddingBlock: 'var(--space-2)',
                          background: 'var(--qalam-warning-100)',
                          color: 'var(--color-warning)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {t('myCodes.noticeBanner', {
                          date: new Date(item.expiresAt).toLocaleDateString(),
                        })}
                      </p>
                    ) : null}
                    {rowFeedback ? (
                      <p
                        data-testid="row-feedback"
                        style={{ margin: 0, color: 'var(--color-success)' }}
                      >
                        {t(rowFeedback.messageKey, {
                          ...(rowFeedback.expiresAt
                            ? {
                                date: new Date(rowFeedback.expiresAt).toLocaleDateString(),
                              }
                            : {}),
                        })}
                      </p>
                    ) : null}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      <button
                        type="button"
                        data-testid="extend-button"
                        disabled={!extendable || busyId === item.id}
                        onClick={() => void extend(item.id)}
                      >
                        {t('myCodes.extend')}
                      </button>
                      <button
                        type="button"
                        data-testid="ask-platform-button"
                        // A queued code has been asked; the button says so by
                        // going away rather than by asking twice.
                        disabled={!canAskPlatform(item.state) || busyId === item.id}
                        onClick={() => void askPlatform(item.id)}
                      >
                        {t('myCodes.askPlatform')}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {actionFailed ? <p data-testid="action-error">{actionFailed}</p> : null}

          <section
            aria-labelledby="codes-share-heading"
            style={{ marginBlockStart: 'var(--space-6)' }}
          >
            <h2 id="codes-share-heading">{t('myCodes.shareTitle')}</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${t('myCodes.shareText')} ${shareUrl}`)}`}
                data-testid="codes-share-whatsapp"
                target="_blank"
                rel="noreferrer"
              >
                {t('myCodes.shareWhatsApp')}
              </a>
              <button
                type="button"
                data-testid="codes-copy-link"
                onClick={() => {
                  void navigator.clipboard?.writeText(shareUrl).then(() => setCopied(true));
                }}
              >
                {t('myCodes.copyLink')}
              </button>
            </div>
            {copied ? <p data-testid="codes-copied">{t('myCodes.copied')}</p> : null}
          </section>
        </>
      )}
    </main>
  );
}
